import { FC, useMemo, useEffect } from 'react'
import styles from 'components/features/future/FutureWhenInput/FutureWhenInput.styl'
import { useFormContext, Controller } from 'react-hook-form'
import { FutureWhen } from 'components/features/future/types'
import { FutureInputDecoration } from 'components/features/future/FutureInputDecoration/FutureInputDecoration'
import { YearMonthInput } from 'components/features/future/FutureWhenInput/YearMonthInput'
import cn from 'classnames'
import { SelectController } from 'components/atoms/inputs/Select/Select'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { memberAgeSelector } from 'selectors/globalSelectors'
import { FutureFieldButtons } from 'components/features/future/FutureField/FutureFieldButtons'
import { maskNumberFromZero } from 'components/atoms/inputs/maskedInput'
import { FutureInputLayout } from 'components/features/future/FutureInputDecoration/FutureInputLayout'
import { FutureInputDecorationError } from 'components/features/future/FutureInputDecoration/FutureInputDecorationError'

const DEFAULT_OPTIONS: FutureWhen['slug'][] = ['in_certain_years', 'at_age', 'at_month']
export const FULL_OPTIONS: FutureWhen['slug'][] = ['as_soon_as_possible', 'in_certain_years', 'at_age', 'at_month']

const OPTIONS = [
  {
    label: 'As soon as possible',
    value: 'as_soon_as_possible' as const,
  },
  {
    label: 'In a certain amount of years',
    value: 'in_certain_years' as const,
  },
  {
    label: 'At a certain age',
    value: 'at_age' as const,
  },
  {
    label: 'At an approximate date',
    value: 'at_month' as const,
  },
]
type OptionValue = typeof OPTIONS[0]['value']


type YearMonthInputWrapperProps = {
  field: {
    onChange: (value: string) => void
    value: string
  }
}

const YearMonthInputWrapper: FC<YearMonthInputWrapperProps> = ({ field: { onChange, value }}) => (
  <YearMonthInput
    onChange={ onChange }
    initialValue={ value }
  />
)

type FutureWhenInputProps = {
  error?: string
  options?: FutureWhen['slug'][]
}

export const FutureWhenInput: FC<FutureWhenInputProps> = (props) => {
  const {
    error,
    options,
  } = props

  const { watch, control, getValues, setValue, clearErrors, trigger } = useFormContext()
  const formSlug = watch('slug')
  const slug = watch('when.slug') as FutureWhen['slug']

  const availableOptions = useMemo(() => {
    const optionsSet = new Set(options || DEFAULT_OPTIONS)
    const slugLocal = getValues('when.slug')
    return OPTIONS.filter((option) => optionsSet.has(option.value) || slugLocal === option.value)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...(options || [])])

  const age = useStoreSelector(memberAgeSelector) || 1

  useEffect(() => {
    if (slug === 'in_certain_years') {
      const value = getValues('when.years')
      clearErrors('when.age')
      clearErrors('when.month')
      trigger('when.years')
      if (value === undefined) {
        setValue('when.years', '1')
      }
    } else if (slug === 'at_age') {
      const value = getValues('when.age')
      clearErrors('when.years')
      clearErrors('when.month')
      trigger('when.age')
      if (value === undefined) {
        setValue('when.age', age + 1)
      }
    } else if (slug === 'at_month') {
      clearErrors('when.years')
      clearErrors('when.age')
    }
  }, [slug, getValues, setValue, clearErrors, age, trigger])

  const inCertainYearsChild = useMemo(() => {
    if (slug !== 'in_certain_years') {
      return null
    }

    return (
      <FutureFieldButtons
        label='In how many years?'
        name='when.years'
        mask={ maskNumberFromZero }

        error={ error }

        min={ 1 }
        max={ 100 }
        step={ 1 }
      />
    )
  }, [error, slug])

  const atAgeChild = useMemo(() => {
    if (slug !== 'at_age') {
      return null
    }

    const note = formSlug !== 'retirement' ? null :
      'Social Security benefits start at age 62, but you won\'t get full benefits unless you retire at 67'

    return (
      <FutureFieldButtons
        label='At what age?'
        name='when.age'
        mask={ maskNumberFromZero }

        note={ note }
        error={ error }

        min={ age }
        max={ 100 }
        step={ 1 }
      />
    )
  }, [age, error, slug, formSlug])

  const errorChild = useMemo(() => {
    if (error) {
      return (
        <FutureInputDecorationError type={ error } />
      )
    }

    return null
  }, [error])

  const atMonthChild = useMemo(() => {
    if (slug !== 'at_month') {
      return null
    }

    const labelChild = (
      <div className={ styles.twoCols }>
        <div className={ styles.col }>
          Month
        </div>
        <div className={ styles.col }>
          Year
        </div>
      </div>
    )

    return (
      <FutureInputLayout
        label={ labelChild }
        error={ errorChild }
      >
        <Controller
          control={ control }
          name='when.month'
          render={ YearMonthInputWrapper as any }
        />
      </FutureInputLayout>
    )
  }, [control, errorChild, slug])

  return (
    <div className={ styles.futureWhenInput }>
      <div className={ cn(styles.whenInput, availableOptions.length > 1 && styles.visible) }>

        <FutureInputDecoration label='When?'>
          <SelectController<OptionValue> options={ availableOptions } name='when.slug' />
        </FutureInputDecoration>
      </div>
      { inCertainYearsChild }
      { atAgeChild }
      { atMonthChild }
    </div>
  )
}
